/**
 * The vocabulary of synchronisation.
 *
 * Everything here is keyed by `accountId` as well as record identity. That is
 * not defensiveness: one iPad is shared between learners, and the failure it
 * prevents — signing in as somebody else and inheriting the previous account's
 * queued operations, cursor or conflicts — would upload one learner's work to
 * another's account.
 */

/** The four kinds the server's sync contract carries. */
export type SyncKind = 'document' | 'lesson_progress' | 'user_settings' | 'practice_session';

/** What the learner is told. Each value is a claim that must be true. */
export type SyncStatus =
  /** Written to this iPad. Not sent anywhere, and nothing is pending. */
  | 'saved-local'
  /** Work is queued or in flight. */
  | 'syncing'
  /** Everything local has been acknowledged by the server. */
  | 'synced'
  /** Cannot reach the server. Work continues and is queued. */
  | 'offline'
  /** Something needs a person: a conflict, or a rejection that will not resolve itself. */
  | 'needs-attention';

/**
 * One queued change.
 *
 * `operationId` is minted once, written to IndexedDB before any network attempt,
 * and reused for every retry and across restarts. It is the server's unit of
 * idempotency: a dropped response is indistinguishable from a dropped request,
 * so the only safe reply to "did that land?" is to ask again with the same id.
 */
export interface OutboxOperation {
  operationId: string;
  accountId: string;
  kind: SyncKind;
  recordId: string;
  payload: Record<string, unknown>;
  title: string;
  feature: string;
  schemaVersion: number;
  /**
   * The remote revision this change was based on, or null when the record is
   * believed absent remotely. Null means "create"; a number means "update only
   * if the server is still at this revision".
   */
  baseRevision: number | null;
  deleted: boolean;
  queuedAt: number;
  /** How many times it has been attempted, for backoff and for honest reporting. */
  attempts: number;
  /** Set when the last attempt failed in a way worth showing a person. */
  lastError?: string;
}

/**
 * What the two sides last agreed on, for one record.
 *
 * Both numbers are needed, and this is the pair that makes conflict detection
 * possible at all:
 *
 *   `revision` is the server's — the base a later change must be sent against.
 *   `localRevision` is this device's revision *at the moment they agreed*.
 *
 * With only the server's number, "the server moved on" and "I edited this while
 * offline" look identical, and the engine has to guess. Comparing the local
 * record's current revision against `localRevision` answers "did this device
 * change anything since we last agreed?" exactly, which is what decides between
 * a safe fast-forward and a conflict that must preserve both sides.
 */
export interface RemoteRevision {
  key: string;
  accountId: string;
  kind: SyncKind;
  recordId: string;
  revision: number;
  localRevision: number | null;
  /** When the server last confirmed it. */
  at: number;
}

/**
 * A recoverable copy kept when local and remote both changed.
 *
 * Both sides are preserved verbatim. Raw ink is never field-merged — there is no
 * correct way to interleave two people's handwriting, and guessing would destroy
 * the thing the app exists to protect.
 */
export interface ConflictRecord {
  id: string;
  accountId: string;
  kind: SyncKind;
  recordId: string;
  /** Human-readable, e.g. "Cmaj7 practice (this iPad)". */
  title: string;
  localPayload: Record<string, unknown>;
  remotePayload: Record<string, unknown>;
  remoteRevision: number;
  detectedAt: number;
  resolved: boolean;
}

/** Per-account sync bookkeeping. */
export interface SyncMetaRecord {
  accountId: string;
  /** Opaque server cursor. Fed back verbatim. */
  cursor: string | null;
  /** False until the first reconciliation for this account has completed. */
  reconciled: boolean;
  lastPulledAt: number | null;
  lastPushedAt: number | null;
}

export function revisionKey(accountId: string, kind: SyncKind, recordId: string): string {
  return `${accountId}|${kind}|${recordId}`;
}

/** A UUID for an operation or conflict id. */
export function newOperationId(): string {
  const crypto = globalThis.crypto as Crypto | undefined;
  if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (crypto && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** What the server sends back for one pushed operation. */
export interface PushResult {
  operation_id: string;
  record_id: string;
  kind?: SyncKind;
  status: 'applied' | 'conflict' | 'rejected';
  revision?: number | null;
  server_payload?: Record<string, unknown> | null;
  message?: string;
}

/** One record from a pull page. */
export interface PulledRecord {
  kind: SyncKind;
  record_id: string;
  payload: Record<string, unknown>;
  title: string;
  feature: string;
  schema_version: number;
  revision: number;
  updated_at: string;
  deleted: boolean;
}

export interface PullPage {
  records: PulledRecord[];
  cursor: string | null;
  has_more: boolean;
}
